import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  describeOperationDevice,
  getPaymentMethodLabel,
} from '@/features/customers/components/CustomerOperations';
import type { CustomerTimelineEntry } from '@/features/customers/types';
import { NewNoteForm, NoteItem } from '@/features/operations/components/NotesTimeline';
import { formatHistoryTimestamp, formatSaleDate } from '@/features/operations/labels';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import { getQuotationStatusLabel } from '@/features/operations/quotationStatus';

type QuotationStatusEntry = Extract<CustomerTimelineEntry, { type: 'QUOTATION_STATUS' }>;
type ReservationEntry = Extract<CustomerTimelineEntry, { type: 'RESERVATION' }>;
type SaleEntry = Extract<CustomerTimelineEntry, { type: 'SALE' }>;
type SaleReversalEntry = Extract<CustomerTimelineEntry, { type: 'SALE_REVERSAL' }>;

/// One step of a quotation or an operation: when and who, what happened, on which phone, and the
/// way in to it.
function TimelineStep({
  caption,
  title,
  phone,
  reason,
  to,
  linkLabel,
}: {
  caption: string;
  title: string;
  phone: string;
  reason: string | null;
  to: string;
  linkLabel: string;
}) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      justifyContent="space-between"
      sx={{ minWidth: 0 }}
    >
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
        <Typography variant="body2">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {phone}
        </Typography>
        {reason ? (
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            Motivo: {reason}
          </Typography>
        ) : null}
      </Stack>
      <Button
        component={RouterLink}
        to={to}
        size="small"
        variant="outlined"
        aria-label={`${linkLabel} ${phone}`}
        sx={{ flexShrink: 0 }}
      >
        {linkLabel}
      </Button>
    </Stack>
  );
}

function QuotationStep({ entry }: { entry: QuotationStatusEntry }) {
  const phone = `${entry.quotation.brand} ${entry.quotation.model}${
    entry.quotation.storageGb === null ? '' : ` · ${entry.quotation.storageGb}GB`
  }`;

  return (
    <TimelineStep
      caption={`${formatHistoryTimestamp(entry.occurredAt)} · ${
        entry.changedBy ? entry.changedBy.fullName : 'Solicitud pública'
      }`}
      title={
        entry.fromStatus
          ? `${getQuotationStatusLabel(entry.fromStatus)} → ${getQuotationStatusLabel(entry.toStatus)}`
          : `Cotización creada · ${getQuotationStatusLabel(entry.toStatus)}`
      }
      phone={phone}
      reason={entry.reason}
      to={`/admin/quotations/${entry.quotation.id}`}
      linkLabel="Ver cotización"
    />
  );
}

/// Who reserved is on the reservation; who cancelled it, in the history of its device.
function reservationAuthor(entry: ReservationEntry): string {
  if (entry.createdBy) {
    return entry.createdBy.fullName;
  }

  return entry.origin === 'WEB' ? 'El comprador, desde la web' : 'Usuario eliminado';
}

function ReservationStep({ entry }: { entry: ReservationEntry }) {
  const when = formatHistoryTimestamp(entry.occurredAt);
  const titles: Record<ReservationEntry['event'], string> = {
    CREATED: `Reserva creada · vence el ${formatHistoryTimestamp(entry.expiresAt)}`,
    CANCELLED: 'Reserva cancelada',
    EXPIRED: 'Reserva vencida',
  };

  return (
    <TimelineStep
      caption={entry.event === 'CREATED' ? `${when} · ${reservationAuthor(entry)}` : when}
      title={titles[entry.event]}
      phone={describeOperationDevice(entry.device)}
      reason={entry.reason}
      to={`/admin/devices/${entry.device.id}`}
      linkLabel="Ver equipo"
    />
  );
}

function SaleStep({ entry }: { entry: SaleEntry }) {
  const { formatNullablePrice } = usePriceDisplay();
  const seller =
    entry.soldBy?.fullName ??
    (entry.paymentMethod === 'MERCADO_PAGO' ? 'Compra online' : 'Usuario eliminado');

  return (
    <TimelineStep
      caption={`${formatHistoryTimestamp(entry.occurredAt)} · ${seller}`}
      title={`Compra del ${formatSaleDate(entry.soldAt, entry.paymentMethod)} por ${formatNullablePrice(
        entry.finalPriceMinor,
      )} · ${getPaymentMethodLabel(entry.paymentMethod)}`}
      phone={describeOperationDevice(entry.device)}
      reason={null}
      to={`/admin/devices/${entry.device.id}`}
      linkLabel="Ver equipo"
    />
  );
}

function SaleReversalStep({ entry }: { entry: SaleReversalEntry }) {
  return (
    <TimelineStep
      caption={`${formatHistoryTimestamp(entry.occurredAt)} · ${
        entry.revertedBy?.fullName ?? 'Usuario eliminado'
      }`}
      title={`Compra del ${formatSaleDate(entry.soldAt, entry.paymentMethod)} revertida`}
      phone={describeOperationDevice(entry.device)}
      reason={entry.reason}
      to={`/admin/devices/${entry.device.id}`}
      linkLabel="Ver equipo"
    />
  );
}

/// Says where each line comes from at a glance: the list mixes them.
const entryOrigins: Record<CustomerTimelineEntry['type'], string> = {
  NOTE: 'Nota',
  QUOTATION_STATUS: 'Cotización',
  RESERVATION: 'Reserva',
  SALE: 'Compra',
  SALE_REVERSAL: 'Compra',
};

/**
 * The customer's notes and the steps of their quotations (CRM-4, #110), reservations and purchases
 * (CRM-6, #230), in one list, oldest first.
 * The form stays below the newest entry, as on a quotation or a device.
 */
export function CustomerTimeline({
  entries,
  isLoading,
  error,
  onRetry,
  onAdd,
  onEdit,
}: {
  entries: CustomerTimelineEntry[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  onAdd: (body: string) => Promise<void>;
  onEdit: (noteId: string, body: string) => Promise<void>;
}) {
  return (
    <Box data-section="timeline">
      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}
      {isLoading ? (
        <Typography variant="body2" color="text.secondary">
          Cargando el historial…
        </Typography>
      ) : null}
      {!isLoading && !error && !entries.length ? (
        <Alert severity="info">
          Todavía no hay notas ni movimientos. Escribí la primera nota abajo: sus cotizaciones,
          reservas y compras van a aparecer acá solas.
        </Alert>
      ) : null}
      {!isLoading && entries.length ? (
        <Stack component="ul" spacing={2} sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {entries.map((entry) => (
            <Stack component="li" key={entry.id} spacing={0.5}>
              <Box>
                <Chip size="small" variant="outlined" label={entryOrigins[entry.type]} />
              </Box>
              {entry.type === 'NOTE' ? (
                <NoteItem component="div" note={entry.note} onEdit={onEdit} />
              ) : null}
              {entry.type === 'QUOTATION_STATUS' ? <QuotationStep entry={entry} /> : null}
              {entry.type === 'RESERVATION' ? <ReservationStep entry={entry} /> : null}
              {entry.type === 'SALE' ? <SaleStep entry={entry} /> : null}
              {entry.type === 'SALE_REVERSAL' ? <SaleReversalStep entry={entry} /> : null}
            </Stack>
          ))}
        </Stack>
      ) : null}
      <NewNoteForm onAdd={onAdd} />
    </Box>
  );
}
